import parse from 'html-react-parser'
import { FC, ReactElement, useEffect, useState, memo, useCallback } from 'react'
import { useSwipeable } from 'react-swipeable'

import { fetchHomeBanners } from '../../../features/homeBanners/homeBannerSlice'
import { useI18n } from '../../../hooks'
import { useAppDispatch, useAppSelector } from '../../../store'
import { debounce } from '../../../utils'
import Button from '../../atoms/buttons/button'
import CustomBanner from './banners/customBanner'
import LoaderBanner from './banners/LoaderBanner'
import MainBanner from './banners/mainBanner'
import './index.css'
export interface MainCardProp {
  item: {
    background: { url: string; alt: string }
    text: string
    path: string
  }
}

interface MainCarouselSlide {
  element: ReactElement
  class: string
}

const MainCarousel: FC = (): ReactElement => {
  const { t } = useI18n()
  const dispatch = useAppDispatch()

  const [slideCurrent, setSlideCurrent] = useState(0)
  const [slides, setSlides] = useState<MainCarouselSlide[]>([
    {
      element: <LoaderBanner />,
      class: 'main-carousel-item-active',
    },
  ])
  const [pause, setPause] = useState<boolean>(false)
  const [showControl, setShowControl] = useState<boolean>(false)
  const [showPlay, setShowPlay] = useState<boolean>(false)

  const { homeBanners: banners, isSuccess, isError } = useAppSelector((state) => state.homeBannerReducer)

  useEffect(() => {
    if (!isSuccess && !isError) dispatch(fetchHomeBanners())
  }, [dispatch, isError, isSuccess])

  const getSlideIndex = (currentIndex: number) => {
    const nextSlideIndex = currentIndex === banners.length - 1 ? 0 : currentIndex + 1
    const preSlideIndex = currentIndex === 0 ? banners.length - 1 : currentIndex - 1
    return { nextSlideIndex, preSlideIndex }
  }

  const setNewSlide = (activeIndex: number) => {
    const newSlides = [...slides]
    newSlides.forEach((slid, index) => {
      if (index === activeIndex) {
        slid.class = 'main-carousel-item-active'
      } else if (index < activeIndex) {
        slid.class = 'main-carousel-item-pre'
      } else {
        slid.class = 'main-carousel-item-next'
      }
    })
    const { nextSlideIndex, preSlideIndex } = getSlideIndex(activeIndex)
    if (newSlides[activeIndex] && newSlides[preSlideIndex] && newSlides[nextSlideIndex]) {
      newSlides[activeIndex].class = 'main-carousel-item-active'
      newSlides[preSlideIndex].class = 'main-carousel-item-pre'
      newSlides[nextSlideIndex].class = 'main-carousel-item-next'
      setSlides(newSlides)
      setSlideCurrent(activeIndex)
    }
  }

  const slideLeft = () => {
    const newIndext = getSlideIndex(slideCurrent).preSlideIndex
    setNewSlide(newIndext)
  }

  const slideRight = () => {
    const newIndext = getSlideIndex(slideCurrent).nextSlideIndex
    setNewSlide(newIndext)
  }

  useEffect(() => {
    if (!isSuccess && !isError) return // We're still fetching/processing
    const newSlides: MainCarouselSlide[] = banners.length
      ? banners.map((b: { name: string; htmlContent: string }, index) => {
          return {
            element: <CustomBanner bannerName={b.name}>{parse(b.htmlContent)}</CustomBanner>,
            class: index === 0 ? 'main-carousel-item-active' : index > 0 ? 'main-carousel-item-next' : 'main-carousel-item-pre',
          }
        })
      : [
          // In case no custom banners were found
          {
            element: <MainBanner />,
            class: 'main-carousel-item-active',
          },
        ]
    if (newSlides.length === 1) setPause(true)
    setSlides(newSlides)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banners])

  let timer: NodeJS.Timeout
  const unPause = debounce(() => setPause(false), 10000)

  useEffect(() => {
    if (!pause) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      timer = setTimeout(slideRight, 10000)
    }
    return () => {
      clearTimeout(timer)
    }
  }, [slideCurrent, slideRight, pause, banners])

  const handleArrowClick = (direction: 'right' | 'left'): void => {
    if (timer) clearTimeout(timer)
    setPause(true)
    unPause()
    if (direction === 'right') slideRight()
    if (direction === 'left') slideLeft()
  }

  const handleDotClick = (index: number) => {
    if (timer) clearTimeout(timer)
    setPause(true)
    unPause()
    setNewSlide(index)
  }

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleArrowClick('right'),
    onSwipedRight: () => handleArrowClick('left'),
  })

  const handlePlay = () => {
    if (showPlay) {
      setPause(false)
      setShowPlay(false)
    } else {
      setPause(true)
      setShowPlay(true)
    }
  }

  const sliderControlHandlers = useCallback(
    () =>
      banners.length > 1
        ? {
            onMouseEnter: () => {
              setShowControl(true)
              setPause(true)
              setShowPlay(true)
            },
            onMouseLeave: () => {
              setShowControl(false)
              setPause(false)
              setShowPlay(false)
            },
            onFocus: () => {
              setShowControl(true)
            },
          }
        : {},
    [banners]
  )
  return (
    <div className="main-carousel" {...sliderControlHandlers()} {...swipeHandlers} style={{ touchAction: 'pan-y' }}>
      {showControl && banners.length > 1 && (
        <Button
          className="btn-wrapper main-carousel-controls fade-in"
          aria-label={showPlay ? t('Common_ButtonPlay') : t('Common_ButtonPause')}
          onClick={() => handlePlay()}
        >
          <i className={`feather-${showPlay ? 'play' : 'pause'} `}></i>
        </Button>
      )}
      {banners.length > 1 && (
        <div className="main-carousel-arrows">
          <button className="main-carousel-arrow-left" aria-label="left-carousel-arrow" onClick={() => handleArrowClick('left')}>
            <i className="feather-chevron-left"></i>
          </button>
          <button className="main-carousel-arrow-right" aria-label="right-carousel-arrow" onClick={() => handleArrowClick('right')}>
            <i className="feather-chevron-right"></i>
          </button>
        </div>
      )}
      <div className="main-carousel-container">
        {slides.map((slide, i) => (
          <div key={`main-slid-${i}`} className={`main-carousel-item ${slide.class}`}>
            {slide.element}
          </div>
        ))}
      </div>
      <div className="main-carousel-dots">
        {slides.map((slide, index) => {
          const className = slide.class.includes('active') ? 'main-carousel-dot-active' : ''
          return (
            <button
              aria-label={t('Common_goToSlide') + ' ' + (index + 1)}
              key={`main-dot-${index}`}
              onClick={() => handleDotClick(index)}
              className={`main-carousel-dot ${className}`}
              title={`card-${index}-selector`}
            ></button>
          )
        })}
      </div>
    </div>
  )
}

export default memo(MainCarousel)
